import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  DEFAULT_FAVICON,
  DEFAULT_TENANT_ID,
  TENANT_STORAGE_KEY,
  TenantBranding,
  extractTenantIdFromHostname,
  resolveDocumentTitle,
  resolveTenantBranding
} from '@core/tenant/tenant.config';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);

  private readonly tenantIdSignal = signal(DEFAULT_TENANT_ID);
  private readonly brandingSignal = signal<TenantBranding>(resolveTenantBranding(DEFAULT_TENANT_ID));

  readonly tenantId = this.tenantIdSignal.asReadonly();
  readonly branding = this.brandingSignal.asReadonly();
  readonly tenantName = computed(() => this.brandingSignal().displayName);

  initialize(): void {
    const hostname = this.document.location?.hostname ?? '';
    const resolvedTenantId = extractTenantIdFromHostname(hostname);
    const branding = resolveTenantBranding(resolvedTenantId);

    this.tenantIdSignal.set(branding.id);
    this.brandingSignal.set(branding);

    localStorage.setItem(TENANT_STORAGE_KEY, branding.id);

    this.document.documentElement.setAttribute('data-tenant', branding.id);
    this.document.body?.setAttribute('data-tenant', branding.id);

    this.applyDocumentIdentity();
    this.applyPalette();
  }

  /**
   * Título da aba e favicon conforme o tenant resolvido. Roda no APP_INITIALIZER,
   * antes do primeiro render, então o usuário não vê o título do index.html piscar.
   */
  private applyDocumentIdentity(): void {
    const branding = this.brandingSignal();

    this.title.setTitle(resolveDocumentTitle(branding));
    this.setFavicon(branding.favicon ?? DEFAULT_FAVICON);
  }

  /**
   * Reaponta o <link rel="icon"> do index.html. Reusa a tag existente quando há
   * uma; só cria se o documento não trouxer nenhuma.
   */
  private setFavicon(href: string): void {
    const head = this.document.head;
    if (!head) return;

    const links = Array.from(
      head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
    );

    if (!links.length) {
      const link = this.document.createElement('link');
      link.rel = 'icon';
      link.href = href;
      head.appendChild(link);
      return;
    }

    // Mantém apenas o primeiro ícone: os demais (16x16/32x32 do tema padrão)
    // apontariam para a marca errada se o tenant tiver favicon próprio.
    links.forEach((link, index) => {
      if (index === 0) {
        link.href = href;
        // O type herdado do .ico invalidaria um href .png.
        link.removeAttribute('type');
        link.removeAttribute('sizes');
      } else {
        link.remove();
      }
    });
  }

  getCurrentTenantId(): string {
    return this.tenantIdSignal();
  }

  getCurrentBranding(): TenantBranding {
    return this.brandingSignal();
  }

  getLogo(isDarkMode: boolean): string {
    const branding = this.brandingSignal();
    return isDarkMode ? branding.logos.dark : branding.logos.light;
  }

  getLoginLogo(isDarkMode: boolean): string {
    const branding = this.brandingSignal();
    return branding.logos.login ?? (isDarkMode ? branding.logos.dark : branding.logos.light);
  }

  applyPalette(isDarkMode: boolean = this.document.body?.classList.contains('dark_mode') ?? false): void {
    const palette = isDarkMode ? this.brandingSignal().palette.dark : this.brandingSignal().palette.light;

    Object.entries(palette).forEach(([property, value]) => {
      this.document.documentElement.style.setProperty(property, value);
      this.document.body?.style.setProperty(property, value);
    });
  }
}